import { useLocation } from 'react-router-dom';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';
import "../styles/PrivacyPolicy.css";

function PrivacyPolicyPage() {
  const { pathname } = useLocation();
  const isZh = pathname.startsWith('/hk');
  return (
    <>
      <Navbar />
      <div className="about-banner-01" />
      <h2 className="form-title text-center" style={{ marginTop: '2rem' }}>
        {isZh ? 'ActiviKid 隱私政策' : 'ActiviKid Privacy Policy'}
      </h2>
      <div className="terms-content">
        <p>
          Last Updated: 1 September 2026
        </p>

        <p>
          Welcome to ActiviKid! This Privacy Policy explains who we are, how ActiviKid (Hong Kong) Limited and our affiliated entities ("ActiviKid", "we", "us", or "our") collect, share, and use personal information about you, and how you can exercise your data privacy rights.
        </p>

        <p>
          This Privacy Policy applies to our website (including https://activikid.hk), our mobile applications, our Admin CMS, our merchant management portals (collectively, the "Services"), and any related features, including our AI-driven recommendation and matching technologies.
        </p>

        <p>
          If you have any questions or concerns regarding our use of your personal information, please contact our Data Protection and Customer Support team at cs@activikid.hk.
        </p>

        <h3 className="form-subtitle">
          <b>Important Notice Regarding Children</b>
        </h3>
        <p>
          The Services are designed and intended to be used by adults aged 18 and older (parents and legal guardians). The Services are not directed to or intended for direct use by children under 18. We do not knowingly collect personal information directly from children, nor can children register for an account.
        </p>
        <p>
          Any information relating to children (such as child profiles, interests, or ages) is provided solely by the parent or legal guardian on their child's behalf to discover and book appropriate activities.
        </p>

        <h3 className="form-subtitle">
          <b>1. What Does ActiviKid Do?</b>
        </h3>
        <p>
          ActiviKid operates an online discovery and booking platform that enables parents and guardians ("Users") to find, compare, book, and pay for extracurricular activities, workshops, holiday camps, and educational courses ("Activities") operated by independent third-party educators and institutions ("Activity Providers" or "Merchants").
        </p>
        <p>
          We utilize recommendation algorithms, natural language processing, and machine learning models to help parents match their child's unique interests and developmental needs with relevant courses.
        </p>

        <h3 className="form-subtitle">
          <b>2. What Personal Information Does ActiviKid Collect?</b>
        </h3>
        <p>
          We collect information you provide directly, information collected automatically during your interaction with our Services, and information from authorized third parties.
        </p>
        <p><b>A. Personal Information You Provide Directly to Us</b></p>
        <ul>
          <li>
            <b>Parent Account Information:</b> When you register for an account, we collect your full name, email address, mobile phone number, and account password.
          </li>
          <li>
            <b>Child Profile Details:</b> To enable personalized course discovery and booking, parents may voluntarily input their child's profile details, including the child's first name/nickname, age/birth month and year, school grade level, subject interests, activity preferences, and developmental milestones.
          </li>
          <li>
            <b>Booking and Transaction Records:</b> When you reserve an Activity, we collect order history, booked schedules, transaction amounts, timestamps, and attendee verification notes.
          </li>
          <li>
            <b>Payment Information (Securely Handled by Yedpay):</b> All payment card and electronic payment transactions are processed directly by certified third-party payment gateways (such as Yedpay). ActiviKid does not store, process, or transmit complete credit card numbers, CVV security codes, or full debit card credentials on our servers. All payment data is encrypted and handled in compliance with PCI-DSS standards by the payment processor.
          </li>
          <li>
            <b>Activity Provider &amp; Merchant Credentials:</b> For educators and institutional partners registering merchant accounts, we collect business registration details, designated contact person names, phone numbers, merchant bank account payout information, and course schedules.
          </li>
          <li>
            <b>User Communications and Reviews:</b> Feedback, reviews, ratings, survey responses, and communications sent to our customer support team or directly to Activity Providers via in-app messaging.
          </li>
        </ul>
        <p><b>B. Personal Information Collected Automatically</b></p>
        <ul>
          <li>
            <b>Device and Technical Data:</b> Internet Protocol (IP) address, unique device identifiers (UUID, IDFA, or Android Advertising ID), device manufacturer, operating system version, browser type, language preferences, and network carrier.
          </li>
          <li>
            <b>Usage &amp; Interaction Data:</b> Course listings viewed, search queries, filter selections, page transition paths, error logs, and session durations.
          </li>
          <li>
            <b>Location Data:</b> Coarse/approximate location derived from your IP address to display regionally relevant extracurricular courses and centers.
          </li>
        </ul>

        <h3 className="form-subtitle">
          <b>3. How Does ActiviKid Use Your Personal Information?</b>
        </h3>
        <p>
          ActiviKid uses collected data for the following specific purposes:
        </p>
        <ul>
          <li>
            <b>Platform Operations &amp; Booking Execution:</b> To authenticate users, process reservations, confirm course bookings, manage merchant payouts, and maintain your family's booking calendar.
          </li>
          <li>
            <b>AI Matching &amp; Recommendation Algorithms:</b> To analyze your child's age, interests, location preferences, and past search queries using recommendation models and natural language processing to deliver personalized course suggestions and curated learning pathways.
          </li>
          <li>
            <b>Service Communications:</b> To send automated booking confirmations, class reminder alerts, schedule changes, receipt notices, and security updates via email, push notification, or SMS.
          </li>
          <li>
            <b>Customer Support:</b> To investigate and resolve customer inquiries, booking discrepancies, and technical issues.
          </li>
          <li>
            <b>Platform Security &amp; Fraud Prevention:</b> To verify user authenticity, prevent fraudulent payments, monitor illegal activities, and enforce our Terms of Service.
          </li>
          <li>
            <b>Legal and Regulatory Compliance:</b> To comply with applicable statutory duties, accounting standards, and lawful law enforcement requests.
          </li>
        </ul>

        <h3 className="form-subtitle">
          <b>4. Use of AI and Recommendation Algorithms</b>
        </h3>
        <ul>
          <li>
            <b>Algorithmic Personalization:</b> ActiviKid uses machine learning and Large Language Model (LLM) tooling to process non-sensitive course metadata, search queries, and parent-submitted child interests to generate accurate recommendations (e.g., matching a 7-year-old interested in robotics with introductory STEM workshops nearby).
          </li>
          <li>
            <b>Automated Processing Safeguards:</b> Algorithmic suggestions are provided solely for informational and exploratory guidance. They do not produce legal, financial, or similarly significant effects.
          </li>
          <li>
            <b>No Public Model Training:</b> We do not sell, trade, or utilize your child's personal identifiers, names, or private communications to train publicly accessible third-party foundation models. Any AI service providers contracted by ActiviKid are bound by strict confidentiality and data-protection obligations to process data strictly on our behalf.
          </li>
        </ul>

        <h3 className="form-subtitle">
          <b>5. Who Does ActiviKid Share Your Personal Information With?</b>
        </h3>
        <p>
          We do not sell, rent, or lease your personal information to third parties. We disclose personal information strictly under the following circumstances:
        </p>
        <ul>
          <li>
            <b>Activity Providers (Merchants):</b> When you book an Activity, we share necessary booking details (such as parent contact name, phone number, and child's name/age) with the specific Activity Provider hosting the session so they can admit your child and provide the service. We never share child data with providers you have not booked with.
          </li>
          <li>
            <b>Third-Party Payment Processors (Yedpay):</b> Billing details and transaction amounts are shared directly with Yedpay to process payments securely.
          </li>
          <li>
            <b>Cloud Infrastructure and Technical Service Providers:</b> We engage reputable cloud hosting platforms, database vendors, transactional email/SMS services, and developer tooling providers under strict contractual data protection agreements.
          </li>
          <li>
            <b>Legal Compliance and Protection:</b> We may disclose personal data if required by Hong Kong law, court subpoena, or governmental regulatory bodies, or to protect the vital safety, property, and rights of ActiviKid, our users, or the general public.
          </li>
          <li>
            <b>Corporate Restructuring:</b> In the event of a merger, acquisition, financing, or sale of company assets, personal data may be transferred as part of that corporate transaction, subject to standard confidentiality protections.
          </li>
        </ul>

        <h3 className="form-subtitle">
          <b>6. How We Protect Children's Data</b>
        </h3>
        <p>
          We recognize the sensitivity of child-related information and maintain heightened data protection measures:
        </p>
        <ul>
          <li>
            Child profile data can only be created and managed by an authenticated parent or guardian.
          </li>
          <li>
            We collect only the minimal data points necessary to facilitate activity recommendations and course admissions (e.g., age, grade level, interests).
          </li>
          <li>
            We strictly prohibit targeted behavioral advertising directed at children.
          </li>
          <li>
            If we become aware that a person under 18 has registered an independent account without verified parental consent, we will immediately deactivate the account and delete all associated records.
          </li>
        </ul>

        <h3 className="form-subtitle">
          <b>7. Account Deletion and Data Retention</b>
        </h3>
        <p><b>A. Mandatory Account and Data Deletion Policy (Apple App Store &amp; Google Play Compliant)</b></p>
        <p>
          We respect your right to control your personal data. You may delete your account and all associated personal information at any time:
        </p>
        <p>
          Users may request deletion of their account and associated personal data at any time by contacting our support team at cs@activikid.hk or via the in-app Account Settings page. Upon verification, all personal identifiers, child profile records, and saved preferences will be permanently removed within 30 days.
        </p>
        <p><b>B. In-App Deletion Steps</b></p>
        <ul>
          <li>Log in to the ActiviKid mobile application.</li>
          <li>Navigate to Profile → Settings → Delete Account.</li>
          <li>Confirm your selection. Once confirmed, your session will terminate immediately and deletion processing will initiate.</li>
        </ul>
        <p><b>C. Data Retention Standard</b></p>
        <p>
          We retain personal data only for as long as necessary to fulfill the purposes outlined in this Policy, or as required by statutory financial, tax, and auditing regulations (such as retaining anonymized financial transaction receipts for statutory record-keeping periods). When information is no longer needed, it is permanently purged or cryptographically anonymized.
        </p>

        <h3 className="form-subtitle">
          <b>8. Data Security Measures</b>
        </h3>
        <p>
          We implement administrative, technical, and physical safeguards designed to protect personal information from unauthorized access, loss, misuse, or alteration. These measures include:
        </p>
        <ul>
          <li>Transport Layer Security (TLS/SSL encryption) for all data in transit across our web and mobile applications.</li>
          <li>Industry-standard database encryption at rest.</li>
          <li>Role-based access controls limiting internal staff access to sensitive customer data on a strict need-to-know basis.</li>
        </ul>
        <p>
          While we apply rigorous security practices, no method of electronic transmission over the Internet or digital storage can be guaranteed 100% impenetrable.
        </p>

        <h3 className="form-subtitle">
          <b>9. International Data Transfers</b>
        </h3>
        <p>
          ActiviKid operates primarily from Hong Kong. However, our servers, third-party cloud hosting providers, and technical infrastructure may be situated internationally (including in the United States, Singapore, and other regions). By utilizing our Services, you acknowledge and consent to the transfer, storage, and processing of your information in jurisdictions outside your place of residence, under contractual protections adhering to internationally accepted privacy standards.
        </p>

        <h3 className="form-subtitle">
          <b>10. Your Data Protection Rights</b>
        </h3>
        <p>
          Under applicable privacy regulations (including the Hong Kong Personal Data (Privacy) Ordinance, Cap. 486), you have the following rights regarding your personal information:
        </p>
        <ul>
          <li>
            <b>Right of Access:</b> You may request a copy of the personal information we hold about you and your registered children.
          </li>
          <li>
            <b>Right to Rectification:</b> You can update or amend inaccurate account or profile details at any time directly through the mobile app or CMS portal.
          </li>
          <li>
            <b>Right to Erasure / Deletion:</b> You can request the total deletion of your profile, child records, and order history as described in Section 7.
          </li>
          <li>
            <b>Right to Withdraw Consent:</b> Where processing is based on consent (such as promotional newsletters or push notifications), you may withdraw consent via device settings or by unsubscribing.
          </li>
          <li>
            <b>Right to Lodge an Inquiry or Complaint:</b> You may contact us at cs@activikid.hk at any time. You also have the right to contact the Office of the Privacy Commissioner for Personal Data (PCPD) Hong Kong.
          </li>
        </ul>

        <h3 className="form-subtitle">
          <b>11. Third-Party Links &amp; Services</b>
        </h3>
        <p>
          Our Services may include web links or integrations with external third-party sites (such as Activity Provider websites or social media platforms). ActiviKid is not responsible for the privacy practices, content, or policies of third-party platforms. We encourage users to review the privacy policies of any third-party websites they visit.
        </p>

        <h3 className="form-subtitle">
          <b>12. Updates to this Privacy Policy</b>
        </h3>
        <p>
          We may update this Privacy Policy from time to time to reflect modifications in our operational practices, technological advancements, or legal and regulatory requirements. When significant revisions are made, we will revise the "Last Updated" date at the top of this document and provide prominent notice via the mobile application or email. Your continued use of the Services after the effective date of an updated policy constitutes your agreement to the revised terms.
        </p>

        <h3 className="form-subtitle">
          <b>13. How to Contact Us</b>
        </h3>
        <p>
          For any inquiries, requests for data access or deletion, or questions regarding our data practices, please contact our team:
        </p>
        <p>
          Email: cs@activikid.hk<br />
          Company: ActiviKid (Hong Kong) Limited
        </p>
      </div>
      <Footer />
    </>
  );
}

export default PrivacyPolicyPage;
